// ============================================================================
// OverviewSection — the headline "at a glance" KPI strip for the business
// report. Reuses the locked StatTile (animated count-up). Each tile renders
// only when its underlying number exists, so a sparse report shows fewer tiles
// rather than fabricated zeros. When income has multiple currencies, one tile
// per currency is rendered (each labelled with its currency code).
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { StatTile } from "../../../components";
import { IconCalendar, IconClock, IconUsers } from "../../../components/icons";
import type { TypedBusinessReport } from "../types";
import { hasIncome } from "../types";
import { currencyPrefix } from "../format";

interface TileSpec {
  key: string;
  label: string;
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  accent: "coral" | "violet" | "ocean" | "lime";
  icon?: React.ReactNode;
}

/** Build the KPI tiles present for this report (only those with data). */
function buildTiles(
  report: TypedBusinessReport,
  t: (k: string) => string,
  locale: string,
): TileSpec[] {
  const tiles: TileSpec[] = [];
  const { sessions, hours, income } = report;

  if (sessions.ok) {
    tiles.push({
      key: "sessions",
      label: t("overview.sessions"),
      value: sessions.total,
      accent: "coral",
      icon: <IconCalendar width={20} height={20} />,
    });
  }

  // Unique clients served this period (busiest-list length is the count).
  const activeClients = sessions.per_client.length;
  if (activeClients > 0) {
    tiles.push({
      key: "clients",
      label: t("overview.clients"),
      value: activeClients,
      accent: "violet",
      icon: <IconUsers width={20} height={20} />,
    });
  }

  if (hours.ok) {
    tiles.push({
      key: "hours",
      label: t("overview.hours"),
      value: hours.total,
      decimals: hours.total % 1 === 0 ? 0 : 1,
      suffix: t("units.hoursSuffix"),
      accent: "ocean",
      icon: <IconClock width={20} height={20} />,
    });
  }

  if (hasIncome(income)) {
    // One tile per currency; label includes the code when there are multiple.
    income.by_currency.forEach((blk) => {
      tiles.push({
        key: `income-${blk.currency}`,
        label:
          income.by_currency.length > 1
            ? `${t("overview.income")} (${blk.currency})`
            : t("overview.income"),
        value: blk.total,
        decimals: 0,
        prefix: currencyPrefix(null, locale, blk.currency_symbol),
        accent: "lime",
      });
    });
  }

  return tiles;
}

export function OverviewSection({ report }: { report: TypedBusinessReport }) {
  const { t, i18n } = useTranslation("businessReport");
  const reduce = useReducedMotion();
  const tiles = buildTiles(report, t, i18n.language);

  if (tiles.length === 0) return null;

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
      className="grid grid-cols-2 gap-4 lg:grid-cols-4"
    >
      {tiles.map((tile) => (
        <StatTile
          key={tile.key}
          label={tile.label}
          value={tile.value}
          decimals={tile.decimals ?? 0}
          prefix={tile.prefix}
          suffix={tile.suffix}
          accent={tile.accent}
          icon={tile.icon}
        />
      ))}
    </motion.div>
  );
}
